import { useEffect, useRef } from "react";
import { Box, LinearProgress, Typography } from "@mui/material";
import MobilePagination from "@/components/mobile/MobilePagination";
import type { RfqBucket, VendorRfqSummary } from "../types";
import { bucketMeta } from "../constants/buckets";
import MobileRfqCard from "./MobileRfqCard";
import RfqBucketChips from "./RfqBucketChips";

interface MobileRfqListProps {
  rfqs: VendorRfqSummary[];
  bucket: RfqBucket;
  counts: Partial<Record<RfqBucket, number>>;
  totalCount: number;
  page: number;
  pageSize: number;
  isFetching: boolean;
  now: number;
  onOpen: (rfq: VendorRfqSummary) => void;
  onBucketChange: (bucket: RfqBucket) => void;
  onPageChange: (page: number) => void;
}

export default function MobileRfqList({
  rfqs,
  bucket,
  counts,
  totalCount,
  page,
  pageSize,
  isFetching,
  now,
  onOpen,
  onBucketChange,
  onPageChange,
}: MobileRfqListProps) {
  const meta = bucketMeta(bucket);
  const totalPages = Math.ceil(totalCount / pageSize);
  const isEmpty = totalCount === 0 && !isFetching;
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    listRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
  }, [page]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <RfqBucketChips counts={counts} selected={bucket} onSelect={onBucketChange} />
      <Box display="flex" alignItems="baseline" gap={1} sx={{ px: 2, pt: 1.5 }}>
        <Typography component="h2" fontWeight={700} fontSize="16px" lineHeight="24px">
          {`${meta.label} (${totalCount})`}
        </Typography>
      </Box>
      {isFetching && <LinearProgress sx={{ mx: 2, mt: 1, borderRadius: "4px" }} />}
      <Box
        ref={listRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          px: 2,
          py: 1,
          opacity: isFetching ? 0.5 : 1,
          transition: "opacity 0.15s",
        }}
      >
        {isEmpty ? (
          <Typography color="textSecondary" sx={{ textAlign: "center", py: 6 }}>
            {meta.empty}
          </Typography>
        ) : (
          rfqs.map((rfq) => <MobileRfqCard key={rfq.id} rfq={rfq} now={now} onOpen={onOpen} />)
        )}
      </Box>
      {totalPages > 1 && (
        <MobilePagination page={page} totalPages={totalPages} onPageChange={onPageChange} totalItems={totalCount} pageSize={pageSize} />
      )}
    </Box>
  );
}
